// The section rule for the FOF home's two charts (Paolo 2026-11-03 18:1x, brainstorm "Two-Chart FOF Row"):
// one path per chart, split at the bars that fall inside the window — the previous exchange close (grey), the 08:40
// decision (drawn in the ON section's colour) and the 12:00 strike (blue) — and each section coloured by its OWN
// drift: green when it ends at or above where it started, red otherwise (swapped when up is bad). The close → decision
// section is "ON". On K_t the strike bar carries the revaluation step: Paolo's colouring — GREEN when the official
// NAV came in BELOW the synth (the revaluation is down), RED when above — and the section after the step is coloured
// by the drift from the official NAV. Pure, so it is unit-tested without the DOM.
import { UP_GOOD, DOWN_BAD } from './sparkline'

export interface SectionBars { closeAt?: number | null; decisionAt?: number | null; markAt?: number | null }
export interface Section { from: number; to: number; colour: string; kind: 'start' | 'on' | 'pre' | 'post' | 'only' }
export interface BarColours { close: string; decision: string | null; strike: string }

export const STRIKE_BLUE = '#6ea8df'
export const CLOSE_GREY = '#8f97a3'

const valid = (f: number | null | undefined, n: number) => f != null && Number.isFinite(f) && f >= 0 && f <= 1 && n >= 2
/** The series index a fraction of the width falls on (the bar at or before it). */
export const indexAt = (frac: number, n: number) => Math.min(n - 1, Math.max(0, Math.floor(frac * (n - 1) + 1e-9)))

/** The step pair's first index: the two consecutive values stepFrom → stepTo, or −1. */
export function stepIndex(values: number[], stepFrom: number | null | undefined, stepTo: number | null | undefined): number {
  if (stepFrom == null || stepTo == null) return -1
  for (let i = 0; i < values.length - 1; i++) if (values[i] === stepFrom && values[i + 1] === stepTo) return i
  return -1
}

/**
 * The sections of the path, by index range, each with its colour. Boundaries: the bars inside the window, in order.
 * A section's drift is its end value against its start value; the section after a revaluation step starts at the
 * step's lower end (the official NAV), never at the pre-step value.
 */
export function sections(values: number[], bars: SectionBars, upIsGood = true, step = -1): Section[] {
  const n = values.length
  if (n < 2) return []
  const up = upIsGood ? UP_GOOD : DOWN_BAD, down = upIsGood ? DOWN_BAD : UP_GOOD
  const marks: { i: number; kind: 'on' | 'pre' | 'post' }[] = []
  if (valid(bars.closeAt, n)) marks.push({ i: indexAt(bars.closeAt!, n), kind: 'on' })
  if (valid(bars.decisionAt, n)) marks.push({ i: indexAt(bars.decisionAt!, n), kind: 'pre' })
  if (valid(bars.markAt, n)) marks.push({ i: step >= 0 ? step + 1 : indexAt(bars.markAt!, n), kind: 'post' })
  marks.sort((a, b) => a.i - b.i)
  const out: Section[] = []
  let from = 0, kind: Section['kind'] = marks.length ? 'start' : 'only'
  for (const m of marks) {
    const stepped = m.kind === 'post' && step >= 0
    const endAt = stepped ? step : m.i          // a stepped strike: the section before it ends at the step's UPPER end
    if (endAt > from) out.push(section(from, endAt, kind))
    from = stepped ? step + 1 : m.i             // ... and the post-strike section starts at its LOWER end (the official NAV)
    kind = m.kind
  }
  if (from < n - 1) out.push(section(from, n - 1, kind))
  return out
  function section(a: number, b: number, k: Section['kind']): Section {
    const start = values[a], end = values[b]
    return { from: a, to: b, colour: end >= start ? up : down, kind: k }
  }
}

/** The bar colours: close grey, decision in the ON section's colour (null when there is no decision bar), strike blue. */
export function barColours(secs: Section[], bars: SectionBars, n: number): BarColours {
  const on = secs.find(s => s.kind === 'on')
  return { close: CLOSE_GREY, decision: valid(bars.decisionAt, n) ? (on?.colour ?? CLOSE_GREY) : null, strike: STRIKE_BLUE }
}

/** Paolo's revaluation colour: green when the NAV came in below the synth (down), red when above. */
export function stepColour(stepFrom: number, stepTo: number, upIsGood = true): string {
  const down = stepTo < stepFrom
  return (down === upIsGood) ? UP_GOOD : DOWN_BAD
}

/** The ON move: the decision value against the close value, as a %; null when either bar is missing. */
export function onPct(closeValue: number | null | undefined, decisionValue: number | null | undefined): number | null {
  if (closeValue == null || decisionValue == null || closeValue <= 0) return null
  return (decisionValue / closeValue - 1) * 100
}
